<template>
  <v-menu
    v-model="dateRangeMenuOpen"
    min-width="auto"
    :disabled="disabled"
    :close-on-content-click="false"
  >
    <template v-slot:activator="{ on, attrs }">
      <v-text-field
        v-model="formattedDateRange"
        append-icon="mdi-calendar"
        class="report-date-range-input"
        readonly
        outlined
        hide-details
        v-bind="attrs"
        v-on="on"
        :label="label"
        @click:append="dateRangeMenuOpen = true"
      />
    </template>

    <v-card
      class="date-picker-card"
      outlined
      rounded
    >
      <v-card-text class="pa-0">
        <v-row no-gutters>
          <v-col
            cols="12"
            sm="3"
          >
            <v-list dense>
              <v-subheader class="pl-4">PRESETS</v-subheader>
              <v-list-item
                v-for="preset in presets"
                :key="preset.label"
                :class="{ 'active': dateRange[0] === preset.range[0] && dateRange[1] === preset.range[1] }"
                @click="usePresetRange(preset)"
              >
                <v-list-item-title>{{ preset.label }}</v-list-item-title>
              </v-list-item>
            </v-list>
          </v-col>

          <v-divider
            vertical
            class="d-none d-sm-block"
          />

          <v-col
            cols="12"
            sm="9"
            class="pl-sm-1"
          >
            <v-date-picker
              v-model="dateRange"
              range
              scrollable
              no-title
              full-width
            />
          </v-col>
        </v-row>
      </v-card-text>

      <v-divider />

      <v-card-actions>
        <v-spacer />
        <v-btn
          text
          @click="dateRangeMenuOpen = false"
        >Close</v-btn>
        <v-btn
          class="btn-primary"
          @click="applyDateRange"
        >Apply</v-btn>
      </v-card-actions>
    </v-card>
  </v-menu>
</template>

<script>
import moment from 'moment';

export default {
  name: 'DateRangePicker',
  props: ['selectedTimeRange', 'label', 'disabled'],
  data() {
    const today = this.formatDate(moment());

    const lastWeekStart = this.formatDate(moment().subtract(1, 'weeks').startOf('week'));
    const lastWeekEnd = this.formatDate(moment().subtract(1, 'weeks').endOf('week'));

    const lastMonthStart = this.formatDate(moment().subtract(1, 'month').startOf('month'));
    const lastMonthEnd = this.formatDate(moment().subtract(1, 'month').endOf('month'));

    const theMonthBeforeLastStart = moment().subtract(2, 'month').startOf('month');
    const theMonthBeforeLastEnd = moment().subtract(2, 'month').endOf('month');

    const lastYearStart = this.formatDate(moment().subtract(1, 'year').startOf('year'));
    const lastYearEnd = this.formatDate(moment().subtract(1, 'year').endOf('year'));

    const ninetyDaysAgo = this.formatDate(moment().subtract(90, 'day'));

    const thisMonthStart = this.formatDate(moment().startOf('month'));
    const thisYearStart = this.formatDate(moment().startOf('year'));

    const lastQuarterStart = moment().subtract(1, 'quarter').startOf('quarter');
    const lastQuarterEnd = moment().subtract(1, 'quarter').endOf('quarter');

    const theQuarterBeforeLastStart = moment().subtract(2, 'quarter').startOf('quarter');
    const theQuarterBeforeLastEnd = moment().subtract(2, 'quarter').endOf('quarter');

    const theQuarterBeforeLast = {
      label: `Q${theQuarterBeforeLastStart.quarter()} ${theQuarterBeforeLastStart.year()}`,
      range: [this.formatDate(theQuarterBeforeLastStart), this.formatDate(theQuarterBeforeLastEnd)],
    };

    const lastQuarter = {
      label: `Q${lastQuarterStart.quarter()} ${lastQuarterStart.year()}`,
      range: [this.formatDate(lastQuarterStart), this.formatDate(lastQuarterEnd)],
    };

    const last90Days = {
      label: 'Last 90 days',
      range: [ninetyDaysAgo, today],
    };

    const monthToDate = {
      label: 'Month to date',
      range: [thisMonthStart, today],
    };

    const yearToDate = {
      label: 'Year to date',
      range: [thisYearStart, today],
    };

    const presets = [
      {
        label: 'Last year',
        range: [lastYearStart, lastYearEnd],
      },
      theQuarterBeforeLast,
      lastQuarter,
      {
        label: `${theMonthBeforeLastStart.format('MMMM')} ${theMonthBeforeLastStart.year()}`,
        range: [this.formatDate(theMonthBeforeLastStart), this.formatDate(theMonthBeforeLastEnd)],
      },
      {
        label: 'Last month',
        range: [lastMonthStart, lastMonthEnd],
      },
      {
        label: 'Last week',
        range: [lastWeekStart, lastWeekEnd],
      },
    ];

    if (
      (lastQuarter.range[0] !== last90Days.range[0] || lastQuarter.range[1] !== last90Days.range[1]) &&
      (yearToDate.range[0] !== last90Days.range[0] || yearToDate.range[1] !== last90Days.range[1])
    ) {
      presets.push(last90Days);
    }

    if (thisYearStart !== today) {
      if (
        this.neitherJanNorMonthStart(
          moment(today),
          moment(thisMonthStart),
          moment(thisYearStart),
        )
      ) {
        // if Jan, use "Year to date"
        // if month start, no "Month to date"
        presets.push(monthToDate);
      }

      if (!this.isStartOfFeb(moment(today))) {
        presets.push(yearToDate); // if start of February, user "Last month"
      }
    }

    return {
      dateRangeMenuOpen: false,
      dateRange: this.selectedTimeRange?.length
        ? this.selectedTimeRange
        : [lastMonthStart, lastMonthEnd],
      presets,
    };
  },
  computed: {
    formattedDateRange() {
      if (!this.dateRange || this.dateRange.length < 2) {
        return '';
      }

      const startDate = this.dateRange[0];
      const endDate = this.dateRange[1];

      return `${this.formatDateForDisplay(startDate)} to ${this.formatDateForDisplay(endDate)}`;
    },
  },
  methods: {
    formatDate(date) {
      return date.format('YYYY-MM-DD');
    },
    formatDateForDisplay(dateStr) {
      const [year, month, day] = dateStr.split('-');
      return `${month}/${day}/${year}`;
    },
    neitherJanNorMonthStart(date, monthStartofDate, yearStartofDate) {
      return monthStartofDate !== yearStartofDate && // not Jan
        monthStartofDate !== date; // not month start
    },
    isStartOfFeb(date) {
      return date.month() === 1 && date.date() === 1;
    },
    emitRangeSelectEvent() {
      const [startDate, endDate] = this.dateRange;
      if (!startDate || !endDate) {
        console.warn('Date range not fully selected.');
        this.loading = false;
        return;
      }

      this.$emit('select-range', { startDate, endDate });
    },
    usePresetRange(preset) {
      this.dateRange = [...preset.range];
      this.dateRangeMenuOpen = false;
      this.emitRangeSelectEvent();
    },
    applyDateRange() {
      this.dateRangeMenuOpen = false;
      this.emitRangeSelectEvent();
    },
  },
  watch: {
    selectedTimeRange(newVal, oldVal) {
      if (newVal[0] === oldVal[0] && newVal[1] === oldVal[1]) {
        return;
      }

      this.dateRange = newVal;
    },
  },

  mounted() {
    this.emitRangeSelectEvent();
  },
};
</script>

<style lang="scss" scoped>
@import '@/scss/variables.scss';
@import '@/scss/mixins/_mobile-nav-spacing.scss';

.report-date-range-input {
  max-width: 400px;

  @media (max-width: $breakpoint-mobile) {
    max-width: 100%;
  }
}

.date-picker-card {
  margin-top: 8px;
  box-shadow: $shadow-lg !important;

  @media (max-width: $breakpoint-mobile) {
    max-width: 90vw;
  }

  ::v-deep .v-date-picker-table {
    height: auto !important;
    padding-bottom: 8px;

    .v-btn {
      margin: 2px 0;
    }
  }

  ::v-deep .v-list {
    max-height: 340px;
    overflow-y: auto;
  }

  ::v-deep .v-list-item {
    cursor: pointer;
    border-radius: 6px;
    margin: 4px 2px;
    padding: 0 8px;
    transition: background-color $transition-fast;

    &.active,
    &:hover {
      background-color: #163958;
      caret-color: #FFFFFF !important;
      color: #FFFFFF !important;
    }

    &:not(.active):hover {
      opacity: 0.7;
    }
  }

  ::v-deep .v-subheader {
    font-weight: 600;
    font-size: 11px;
    color: #718096;
  }
}
</style>
